/**
 * atlasManager — Sprint 2
 * The private atlas: a derived, cached aggregate over the traveller's SEALED
 * trips. Personal figures and personal records only — never a leaderboard, never
 * a position relative to anyone else.
 *
 * Source of truth is the trips themselves; the atlas is a reconstructible cache.
 * Recompute is idempotent and cheap: counts only, no photos, no network.
 *
 * Stored under a DEDICATED key in the userProfile store (NOT inside the profile
 * object, which aggregateProfile rewrites wholesale and would clobber).
 */
import storageManager from './storageManager';
import { poiSlug } from './reveal';

const ATLAS_KEY = 'atlas';

const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

function tripDays(trip) {
  const d = trip?.dates || {};
  if (typeof d.totalDays === 'number' && d.totalDays > 0) return d.totalDays;
  if (d.startDate && d.endDate) {
    const ms = new Date(d.endDate) - new Date(d.startDate);
    if (ms >= 0) return Math.floor(ms / 86400000) + 1;
  }
  return 1;
}

/** Build the atlas object from a list of sealed trips. Pure + cheap. */
export function computeAtlas(sealedTrips) {
  const cityByKey = new Map();      // normKey -> display name
  const countryByKey = new Map();
  const citiesPerCountry = new Map(); // countryKey -> Set(cityKey)
  const revealed = new Set();
  const whispered = new Set();

  let daysTravelled = 0;
  let memories = 0;
  let reflections = 0;
  let offRouteMemories = 0;
  let threadsOpen = 0;
  let threadsResolved = 0;

  let longestTripDays = null;
  let mostMemoriesInTrip = null;
  let firstSealedAt = null;
  let lastSealedAt = null;

  for (const trip of sealedTrips) {
    const city = trip?.destination?.city || '';
    const country = trip?.destination?.country || '';
    const ck = norm(city), nk = norm(country);
    if (city) cityByKey.set(ck, city);
    if (country) countryByKey.set(nk, country);
    if (city && country) {
      if (!citiesPerCountry.has(nk)) citiesPerCountry.set(nk, new Set());
      citiesPerCountry.get(nk).add(ck);
    }

    const days = tripDays(trip);
    daysTravelled += days;

    const notes = trip?.notes || [];
    let tripMemories = 0;
    for (const n of notes) {
      if (n.isActivity || n.isChallenge) continue;
      tripMemories++;
      if (n.offRoute) offRouteMemories++;
    }
    memories += tripMemories;
    reflections += (trip?.reflections || []).length;

    for (const id of (trip?.revealedPois || [])) revealed.add(id);
    for (const id of (trip?.surfacedPois || [])) whispered.add(id);

    for (const th of (trip?.openThreads || [])) {
      if (th?.resolvedTripId) threadsResolved++; else threadsOpen++;
    }

    // Personal records (figures about oneself; the tying trip is recorded)
    if (!longestTripDays || days > longestTripDays.value) {
      longestTripDays = { value: days, tripId: trip.tripId, label: city };
    }
    if (!mostMemoriesInTrip || tripMemories > mostMemoriesInTrip.value) {
      mostMemoriesInTrip = { value: tripMemories, tripId: trip.tripId, label: city };
    }
    if (trip.sealedAt) {
      if (!firstSealedAt || trip.sealedAt < firstSealedAt) firstSealedAt = trip.sealedAt;
      if (!lastSealedAt || trip.sealedAt > lastSealedAt) lastSealedAt = trip.sealedAt;
    }
  }

  // Most cities visited within a single country
  let mostCitiesInCountry = null;
  for (const [nk, set] of citiesPerCountry) {
    if (!mostCitiesInCountry || set.size > mostCitiesInCountry.value) {
      mostCitiesInCountry = { country: countryByKey.get(nk) || nk, value: set.size };
    }
  }

  return {
    updatedAt: new Date().toISOString(),
    cities: [...cityByKey.values()],
    countries: [...countryByKey.values()],
    tripsSealed: sealedTrips.length,
    daysTravelled,
    memories,
    reflections,
    gemsRevealed: revealed.size,
    gemsWhispered: whispered.size,
    offRouteMemories,
    threadsOpen,
    threadsResolved,
    records: {
      ...(longestTripDays ? { longestTripDays } : {}),
      ...(mostMemoriesInTrip ? { mostMemoriesInTrip } : {}),
      ...(mostCitiesInCountry ? { mostCitiesInCountry } : {}),
      ...(firstSealedAt ? { firstSealedAt } : {}),
      ...(lastSealedAt ? { lastSealedAt } : {}),
    },
  };
}

// ── IndexedDB (userProfile store, dedicated 'atlas' key) ────────────────────

async function loadAllTrips() {
  try {
    await storageManager.initialize();
    const db = storageManager.db;
    return new Promise((resolve) => {
      const tx = db.transaction('trips', 'readonly');
      const req = tx.objectStore('trips').getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    });
  } catch { return []; }
}

async function saveAtlasRecord(atlas) {
  try {
    await storageManager.initialize();
    const db = storageManager.db;
    return new Promise((resolve) => {
      const tx = db.transaction('userProfile', 'readwrite');
      tx.objectStore('userProfile').put(atlas, ATLAS_KEY);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch { return false; }
}

async function loadAtlasRecord() {
  try {
    await storageManager.initialize();
    const db = storageManager.db;
    return new Promise((resolve) => {
      const tx = db.transaction('userProfile', 'readonly');
      const req = tx.objectStore('userProfile').get(ATLAS_KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
}

/** Recompute the atlas from every sealed trip and cache it. Idempotent. */
export async function recomputeAtlas() {
  const trips = await loadAllTrips();
  const sealed = trips.filter(t => t && t.sealedAt);
  const atlas = computeAtlas(sealed);
  await saveAtlasRecord(atlas);
  window.__cachedAtlas = atlas;
  return atlas;
}

/** Fast read — cached atlas, or load from IndexedDB (does not recompute). */
export async function getAtlas() {
  if (window.__cachedAtlas) return window.__cachedAtlas;
  const atlas = await loadAtlasRecord();
  if (atlas) window.__cachedAtlas = atlas;
  return atlas;
}

/** Overwrite the cached/stored atlas (used by backup restore). */
export async function setAtlas(atlas) {
  if (!atlas) return false;
  await saveAtlasRecord(atlas);
  window.__cachedAtlas = atlas;
  return true;
}
